import type { SyntheticEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Play } from "lucide-react";
import { outlineButtonClass } from "../ui";
import { LocalImg } from "../../offline/LocalMedia";
import { usePreloadImages } from "../../lib/preloadImages";
import { useLocalAsset } from "../../offline/useLocalAsset";
import { ManualCompleteButton } from "./ManualCompleteButton";
import { useCompletion } from "./useCompletion";
import { useViewTrail } from "./useViewTrail";
import { useWatchSession } from "./useWatchSession";
import { contentOf } from "./types";
import type { AudioContent, AudioSlidesContent, RendererProps, VideoContent } from "./types";

// A timeupdate further apart than this is a jump (a seek), not playback.
const MAX_PLAYBACK_STEP_SECONDS = 2;

const watchedKey = (itemID: string) => `watched-seconds:${itemID}`;

function loadWatched(itemID: string): Set<number> {
  try {
    const stored = JSON.parse(localStorage.getItem(watchedKey(itemID)) || "[]");
    return new Set(Array.isArray(stored) ? stored.filter(Number.isInteger) : []);
  } catch {
    return new Set();
  }
}

function saveWatched(itemID: string, watched: Set<number>): void {
  try {
    localStorage.setItem(watchedKey(itemID), JSON.stringify([...watched]));
  } catch {
    // Storage blocked: progress still counts for this visit.
  }
}

export function VideoRenderer({ item, moduleID, onComplete }: RendererProps) {
  const { t } = useTranslation();
  const content = contentOf<VideoContent>(item);
  const completion = useCompletion(item, moduleID, onComplete);
  const videoSrc = useLocalAsset(content.video_url);
  const posterSrc = useLocalAsset(content.poster_url || undefined);
  const subtitleSrc = useLocalAsset(content.subtitle_url);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [started, setStarted] = useState(false);
  // Which whole seconds of the video were actually played. Completion is the
  // share of the video watched, so dragging the bar to 80% earns nothing: only
  // seconds reached by normal playback are counted.
  const watched = useRef<Set<number>>(new Set());
  const lastTime = useRef<number | null>(null);
  const watch = useWatchSession(item, moduleID, videoSrc, {
    itemUniqueSeconds: () => watched.current.size,
    previouslyWatched: () => watched.current,
  });

  useEffect(() => {
    watched.current = loadWatched(item.id);
    return () => saveWatched(item.id, watched.current);
  }, [item.id]);
  const uploaded = content.source === "uploaded" || !content.source;

  function onTimeUpdate(event: SyntheticEvent<HTMLVideoElement>) {
    const element = event.currentTarget;
    const now = element.currentTime;
    const previous = lastTime.current;
    lastTime.current = now;
    if (
      element.duration &&
      previous !== null &&
      !element.seeking &&
      now >= previous &&
      now - previous <= MAX_PLAYBACK_STEP_SECONDS
    ) {
      const before = watched.current.size;
      for (let second = Math.floor(previous); second <= Math.floor(now); second += 1) {
        watched.current.add(second);
      }
      if (watched.current.size !== before) {
        const percent = Math.min(100, (watched.current.size / Math.ceil(element.duration)) * 100);
        completion.reportMediaProgress(percent);
        if (watched.current.size % 10 === 0) saveWatched(item.id, watched.current);
      }
    }
    watch.onTimeUpdate(event);
  }

  return (
    <section className="flex flex-col gap-3">
      {uploaded ? (
        <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-app-ink shadow-card">
          <video
            ref={videoRef}
            controls
            preload="metadata"
            playsInline
            src={videoSrc}
            poster={posterSrc}
            className="h-full w-full object-cover"
            onPlay={(event) => {
              setStarted(true);
              watch.onPlay(event);
            }}
            onPause={watch.onPause}
            onEnded={(event) => {
              watch.onEnded(event);
              saveWatched(item.id, watched.current);
            }}
            onSeeking={() => {
              lastTime.current = null;
              watch.onSeeking();
            }}
            onSeeked={watch.onSeeked}
            onTimeUpdate={onTimeUpdate}
          >
            {subtitleSrc && <track kind="subtitles" src={subtitleSrc} default />}
          </video>
          {!started && (
            <>
              <div className="pointer-events-none absolute inset-0 bg-linear-to-t from-app-ink/60 via-app-ink/10 to-transparent" />
              <button
                type="button"
                aria-label={t("playVideo")}
                className="absolute inset-0 m-auto grid size-16 place-items-center rounded-full bg-surface/90 text-app-ink shadow-card transition-transform hover:scale-105 active:scale-90"
                onClick={() => void videoRef.current?.play()}
              >
                <Play size={32} fill="currentColor" className="ms-1" />
              </button>
            </>
          )}
        </div>
      ) : (
        <VideoEmbed provider={content.source ?? ""} url={content.video_url || ""} />
      )}
      {content.caption && <p className="text-sm text-app-copy">{content.caption}</p>}
      <ManualCompleteButton
        done={completion.done}
        onClick={() => void completion.complete()}
        rule={item.completion_rule}
      />
    </section>
  );
}

function VideoEmbed({ provider, url }: { provider: string; url: string }) {
  const embedURL = toEmbedURL(provider, url);
  if (!embedURL) return <p className="text-sm text-accent-strong">Unsupported video URL.</p>;
  return (
    <div className="aspect-video w-full overflow-hidden rounded-md bg-black">
      <iframe
        className="h-full w-full"
        src={embedURL}
        title="Video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        sandbox="allow-scripts allow-same-origin allow-presentation"
        allowFullScreen
      />
    </div>
  );
}

function toEmbedURL(provider: string, url: string): string | null {
  if (provider === "youtube") {
    const match = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  }
  if (provider === "vimeo") {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? `https://player.vimeo.com/video/${match[1]}` : null;
  }
  return null;
}

export function AudioRenderer({ item, moduleID, onComplete }: RendererProps) {
  const content = contentOf<AudioContent>(item);
  const completion = useCompletion(item, moduleID, onComplete);
  const audioSrc = useLocalAsset(content.audio_url);
  const watch = useWatchSession(item, moduleID, audioSrc);
  return (
    <section className="grid gap-4">
      {content.cover_image_url && (
        <LocalImg src={content.cover_image_url} alt="" className="w-full rounded-md" />
      )}
      <audio
        controls
        src={audioSrc}
        className="w-full"
        onPlay={watch.onPlay}
        onPause={watch.onPause}
        onSeeking={watch.onSeeking}
        onSeeked={watch.onSeeked}
        onEnded={(event) => {
          watch.onEnded(event);
          completion.reportMediaProgress(100);
        }}
        onTimeUpdate={(event) => {
          const element = event.currentTarget;
          watch.onTimeUpdate(event);
          if (element.duration) {
            completion.reportMediaProgress((element.currentTime / element.duration) * 100);
          }
        }}
      />
      {content.caption && <p className="text-sm text-app-copy">{content.caption}</p>}
      <ManualCompleteButton
        done={completion.done}
        onClick={() => void completion.complete()}
        rule={item.completion_rule}
      />
    </section>
  );
}

export function AudioSlidesRenderer({ item, moduleID, onComplete }: RendererProps) {
  const content = contentOf<AudioSlidesContent>(item);
  const slides = content.slides || [];
  const [activeSlide, setActiveSlide] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const completion = useCompletion(item, moduleID, onComplete);
  const audioSrc = useLocalAsset(content.audio_url);
  // Same visit-by-visit watch record as plain audio, plus which slide was up.
  const watch = useWatchSession(item, moduleID, audioSrc);
  const slideViewed = useViewTrail(item, moduleID, "slide_changed", slides.length);
  usePreloadImages(slides.map((slide) => slide.image_url));

  useEffect(() => {
    if (slides.length > 0) slideViewed(0, "open");
    // Only when the item opens: later changes are reported where they happen.
  }, [item.id]);

  function onTimeUpdate(event: SyntheticEvent<HTMLAudioElement>) {
    watch.onTimeUpdate(event);
    const time = event.currentTarget.currentTime;
    let next = 0;
    for (let index = 0; index < slides.length; index += 1) {
      if (time >= (slides[index].timestamp_seconds || 0)) next = index;
    }
    setActiveSlide(next);
    slideViewed(next, "audio", { position_seconds: Math.round(time * 10) / 10 });
    if (event.currentTarget.duration) {
      completion.reportMediaProgress((time / event.currentTarget.duration) * 100);
    }
  }

  function seekToSlide(index: number) {
    const target = slides[index];
    if (target && audioRef.current) audioRef.current.currentTime = target.timestamp_seconds || 0;
    const shown = Math.min(Math.max(index, 0), Math.max(slides.length - 1, 0));
    setActiveSlide(shown);
    slideViewed(shown, "teacher", { position_seconds: target?.timestamp_seconds || 0 });
  }

  const current = slides[activeSlide];
  return (
    <section className="grid gap-4">
      {current && (
        <figure className="m-0">
          <LocalImg
            className="w-full rounded-md bg-app-field"
            src={current.image_url}
            alt={current.title || `Slide ${activeSlide + 1}`}
          />
          {current.title && (
            <figcaption className="mt-1 text-sm text-app-copy">{current.title}</figcaption>
          )}
        </figure>
      )}
      <div className="flex justify-between gap-3">
        <button
          type="button"
          className={outlineButtonClass}
          disabled={activeSlide === 0}
          onClick={() => seekToSlide(activeSlide - 1)}
        >
          Previous slide
        </button>
        <button
          type="button"
          className={outlineButtonClass}
          disabled={activeSlide >= slides.length - 1}
          onClick={() => seekToSlide(activeSlide + 1)}
        >
          Next slide
        </button>
      </div>
      <audio
        ref={audioRef}
        controls
        src={audioSrc}
        className="w-full"
        onPlay={watch.onPlay}
        onPause={watch.onPause}
        onSeeking={watch.onSeeking}
        onSeeked={watch.onSeeked}
        onTimeUpdate={onTimeUpdate}
        onEnded={(event) => {
          watch.onEnded(event);
          completion.reportMediaProgress(100);
        }}
      />
      {content.caption && <p className="text-sm text-app-copy">{content.caption}</p>}
      <ManualCompleteButton
        done={completion.done}
        onClick={() => void completion.complete()}
        rule={item.completion_rule}
      />
    </section>
  );
}
